/**
 * Os planos de um aluno.
 *
 * Fica pendurado no vínculo, e não numa lista global de planos, porque é assim
 * que o profissional pensa: "os planos do Bruno", não "todos os meus planos".
 * A tabela concorda — o plano pertence ao acompanhamento.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import type { Plan } from '@/features/plans/api';
import { endOfWeeks } from '@/features/plans/agenda';
import { planErrorKey, useCreatePlan, usePlans } from '@/features/plans/usePlans';

/** Quantas semanas o formulário oferece. Doze é um trimestre — além disso, o
 *  profissional monta outro plano em vez de estender este. */
const SEMANAS = [1, 2, 4, 6, 8, 12] as const;

function hoje(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function StudentPlansPage() {
  const { t } = useTranslation(['plans', 'common']);
  const { relationshipId = '' } = useParams();
  const lista = usePlans(relationshipId);

  return (
    <div className="px-5 py-6">
      <Link to={`/students/${relationshipId}`} className="mh-button mh-button--ghost -ml-5">
        {t('common:action.back')}
      </Link>

      <h1 className="mt-3 text-3xl">{t('plans:title')}</h1>
      <p className="mt-1 text-(--color-sand-700)">{t('plans:subtitle')}</p>

      <NovoPlano relationshipId={relationshipId} />

      {lista.isError ? (
        <div className="mt-5">
          <Alert tone="error" live="assertive">
            {t(planErrorKey(lista.error))}
          </Alert>
        </div>
      ) : null}

      {lista.isPending ? (
        <p className="mt-6 text-(--color-sand-700)" aria-busy="true">
          {t('common:state.loading')}
        </p>
      ) : null}

      {lista.isSuccess ? (
        lista.data.items.length === 0 ? (
          <p className="mt-6 text-(--color-sand-700)">{t('plans:empty')}</p>
        ) : (
          <ul className="mt-5 flex flex-col gap-3">
            {lista.data.items.map((plano) => (
              <li key={plano.plan_id}>
                <PlanoCard plano={plano} />
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

function PlanoCard({ plano }: { plano: Plan }) {
  const { i18n } = useTranslation(['plans']);
  const datas = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });

  const periodo = plano.end_date
    ? `${datas.format(new Date(`${plano.start_date}T00:00:00Z`))} – ${datas.format(
        new Date(`${plano.end_date}T00:00:00Z`),
      )}`
    : datas.format(new Date(`${plano.start_date}T00:00:00Z`));

  return (
    <Link
      to={`/plans/${plano.plan_id}`}
      className="block rounded-2xl border border-(--color-sand-200) bg-(--color-sand-0) p-4"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="font-semibold text-(--color-sand-900)">{plano.title}</span>
        <PlanBadge status={plano.status} />
      </span>
      <span className="mt-1 block text-xs text-(--color-sand-600)">{periodo}</span>
    </Link>
  );
}

export function PlanBadge({ status }: { status: Plan['status'] }) {
  const { t } = useTranslation(['plans']);

  // Cor **e** palavra, nunca cor sozinha (docs/06_DESIGN_SYSTEM.md §2).
  const estilo: Record<Plan['status'], string> = {
    draft: 'bg-(--color-sand-100) text-(--color-sand-800)',
    published: 'bg-(--color-mint-100) text-(--color-mint-700)',
    archived: 'bg-(--color-sand-200) text-(--color-sand-700)',
  };

  return (
    <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${estilo[status]}`}>
      {t(`plans:status.${status}`)}
    </span>
  );
}

/**
 * Criar pede início e **número de semanas**, não data de fim.
 *
 * A API aceita plano sem fim, e a tela não oferece: um plano aberto não tem
 * agenda desenhável — quantos dias mostrar? Perguntar "quantas semanas"
 * responde isso e é como o profissional já pensa o ciclo.
 */
function NovoPlano({ relationshipId }: { relationshipId: string }) {
  const { t } = useTranslation(['plans', 'common']);
  const navigate = useNavigate();
  const criar = useCreatePlan();

  const [aberto, setAberto] = useState(false);
  const [title, setTitle] = useState('');
  const [start, setStart] = useState(hoje());
  const [semanas, setSemanas] = useState(4);

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="mh-button mh-button--secondary mt-4"
      >
        {t('plans:create.open')}
      </button>
    );
  }

  return (
    <section className="mt-4 rounded-2xl border border-(--color-sand-200) p-4">
      <h2 className="text-lg font-semibold">{t('plans:create.title')}</h2>

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('plans:field.title')}
        </span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={160}
          autoFocus
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('plans:field.start')}
        </span>
        <input
          type="date"
          value={start}
          onChange={(event) => setStart(event.target.value)}
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('plans:field.weeks')}
        </span>
        <select
          value={semanas}
          onChange={(event) => setSemanas(Number(event.target.value))}
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) bg-(--color-sand-0) px-3 py-3"
        >
          {SEMANAS.map((quantas) => (
            <option key={quantas} value={quantas}>
              {t('plans:unit.weeks', { count: quantas })}
            </option>
          ))}
        </select>
      </label>

      {criar.isError ? (
        <div className="mt-3">
          <Alert tone="error" live="assertive">
            {t(planErrorKey(criar.error))}
          </Alert>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={title.trim() === '' || start === '' || criar.isPending}
          onClick={() =>
            criar.mutate(
              {
                relationshipId,
                draft: {
                  title: title.trim(),
                  start_date: start,
                  end_date: endOfWeeks(start, semanas),
                  // Nasce sem agenda: os dias se marcam na próxima tela.
                  workouts: [],
                },
              },
              { onSuccess: (novo) => navigate(`/plans/${novo.plan_id}`) },
            )
          }
          className="mh-button mh-button--primary"
        >
          {t('plans:create.submit')}
        </button>
        <button
          type="button"
          onClick={() => setAberto(false)}
          className="mh-button mh-button--ghost"
        >
          {t('common:action.cancel')}
        </button>
      </div>
    </section>
  );
}
